/**
 * The tenant's brand theme (TMMAPP-0035) — saas-auth's resolved theme document, v1
 * (`docs/handoffs/REPLY-from-saas-auth-brand-theme-2026-09-24.md`, the binding contract). Every
 * colour role arrives resolved and contrast-checked for both modes; the app derives no colour.
 *
 * `GET {authOrigin}/api/branding/theme?host=<hostname>` is public and pre-auth, so it is issued
 * with `fetch` and `credentials: 'omit'` (like `/api/bootstrap`), never the axios factory. The
 * document is public, non-personal data: the last good copy is cached per host in `localStorage`
 * so a repeat visit paints instantly. A document that fails validation is rejected WHOLE — the
 * caller keeps the cached or built-in theme, never a partial one.
 */
// The vendored platform-default document, not `PLATFORM_DEFAULT_THEME`: that module normalizes
// this document through this one, so importing it here would be a cycle.
import platformDefault from './fixtures/platform-default.v1.json';

export const BRAND_THEME_SCHEMA_VERSION = 1;

/**
 * The colour roles of `modes.light` / `modes.dark`: the 50 of REPLY §2, every one always present,
 * plus `scrim` (SASATH-0350, TMMAPP-0040) and `brandBoundary` (SASATH-0352, TMMAPP-0045), which an
 * older document does not carry.
 */
export const ROLE_NAMES = [
  'bg',
  'surface',
  'surface2',
  'surfaceRaised',
  'text',
  'text2',
  'text3',
  'link',
  'line',
  'lineStrong',
  'hover',
  'selected',
  'focusRing',
  'brand',
  'brandHover',
  'brandInk',
  'brandSoft',
  'brandDeep',
  'brandDeepInk',
  'brandDeepMuted',
  'brandBoundary',
  'accent',
  'accentHover',
  'accentInk',
  'accentSoft',
  'appBar',
  'appBarInk',
  'successFg',
  'successText',
  'successFill',
  'successInk',
  'successSoft',
  'warningFg',
  'warningText',
  'warningFill',
  'warningInk',
  'warningSoft',
  'errorFg',
  'errorText',
  'errorFill',
  'errorInk',
  'errorSoft',
  'infoFg',
  'infoText',
  'infoFill',
  'infoInk',
  'infoSoft',
  'moneyPositive',
  'moneyNegative',
  'moneyNegativeWash',
  'highlight',
  'scrim',
] as const;

export type RoleName = (typeof ROLE_NAMES)[number];
export type BrandMode = 'light' | 'dark';
export type BrandRoles = Record<RoleName, string>;

/** The translucent state layers, painted over a surface. */
export const STATE_LAYER_ROLES: readonly RoleName[] = ['hover', 'selected'];

/** The only roles served as `#rrggbbaa`: the state layers and the modal `scrim`. */
export const TRANSLUCENT_ROLES: readonly RoleName[] = [...STATE_LAYER_ROLES, 'scrim'];

/**
 * Roles a v1 document may omit (additive, no `schemaVersion` bump): absent — an older document or
 * a cached one — they take their `ROLE_FALLBACK` role of the same document and mode, else the
 * built-in default's value for that mode; present, they are validated like any other role.
 */
export const OPTIONAL_ROLES: readonly RoleName[] = ['scrim', 'brandBoundary'];

/**
 * `brandBoundary` (SASATH-0352) is derived per document, so the built-in default's value would be
 * another brand's colour. Absent, it is the document's own `link` for that mode — guaranteed 4.5:1
 * on every canvas, so a pre-0352 document keeps a 3:1 non-text boundary.
 */
const ROLE_FALLBACK: Partial<Record<RoleName, RoleName>> = { brandBoundary: 'link' };

export interface CategoricalColor {
  fill: string;
  ink: string;
  soft: string;
  line: string;
}

export interface Elevation {
  card: string;
  raised: string;
  dialog: string;
}

export interface Radius {
  style: string;
  control: number;
  card: number;
  dialog: number;
}

export interface BrandFont {
  family: string;
  stack: string;
  source: 'google' | 'system';
  cssUrl: string | null;
  files: string[];
  weights: number[];
}

export interface BrandAssets {
  logo: string | null;
  logoReversed: string | null;
  mark: string | null;
  favicon: { svg: string | null; png: string | null };
  appleTouchIcon: string | null;
  maskableIcon: string | null;
  hero: string | null;
  onboardingIllustration: { light: string | null; dark: string | null };
  emailHeader: string | null;
  socialImage: string | null;
}

export const COPY_KEYS = [
  'productName',
  'productShortName',
  'loginTitle',
  'tagline',
  'heroCopy',
  'location',
  'welcomeHeadline',
  'supportEmail',
] as const;

export type CopyKey = (typeof COPY_KEYS)[number];
export type BrandCopyStrings = Record<CopyKey, string | null>;

export interface BrandCopy {
  defaultLocale: string;
  locales: Record<string, BrandCopyStrings>;
  untranslated: Record<string, string[]>;
}

export interface ContrastFailure {
  mode: string;
  fg: string;
  bg: string;
  ratio: number;
  required: number;
}

export interface BrandTheme {
  schemaVersion: 1;
  themeVersion: string;
  updatedAt: string | null;
  source: string;
  modes: Record<BrandMode, BrandRoles>;
  categorical: Record<BrandMode, CategoricalColor[]>;
  elevation: Record<BrandMode, Elevation>;
  radius: Radius;
  fonts: { body: BrandFont; heading: BrandFont; mono: BrandFont };
  assets: BrandAssets;
  copy: BrandCopy;
  contrast: { ok: boolean; failures: ContrastFailure[] };
}

const OPAQUE = /^#[0-9a-f]{6}$/i;
const TRANSLUCENT = /^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/i;

/**
 * Thrown inside the normalizer only; `validateBrandTheme` turns it into a rejection reason. An
 * empty message is filled in by the enclosing `section`.
 */
class Malformed extends Error {}

function fail(reason = ''): never {
  throw new Malformed(reason);
}

/** Read one part of the document; an unnamed failure inside it becomes `invalid <name>`. */
function section<T>(name: string, read: () => T): T {
  try {
    return read();
  } catch (error) {
    if (error instanceof Malformed && error.message === '') fail(`invalid ${name}`);
    throw error;
  }
}

function obj(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail();
  return value as Record<string, unknown>;
}

function str(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) fail();
  return value;
}

function nullableStr(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') fail();
  return value;
}

/** An asset or stylesheet URL: absolute `https:` or root-relative; anything else is dropped. */
function url(value: unknown): string | null {
  const raw = nullableStr(value);
  if (raw === null) return null;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  try {
    return new URL(raw).protocol === 'https:' ? raw : null;
  } catch {
    return null;
  }
}

function colour(value: unknown, translucent: boolean): string {
  const raw = str(value);
  if (!(translucent ? TRANSLUCENT : OPAQUE).test(raw)) fail();
  return raw.toLowerCase();
}

function px(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) fail();
  return value;
}

function roles(value: unknown, mode: BrandMode): BrandRoles {
  const source = section(`mode '${mode}'`, () => obj(value));
  const out = {} as BrandRoles;
  const absent: RoleName[] = [];
  for (const role of ROLE_NAMES) {
    if (source[role] === undefined || source[role] === null) {
      if (!OPTIONAL_ROLES.includes(role)) fail(`missing role '${role}' in mode '${mode}'`);
      absent.push(role);
      continue;
    }
    out[role] = section(`role '${role}' in mode '${mode}'`, () =>
      colour(source[role], TRANSLUCENT_ROLES.includes(role)),
    );
  }
  for (const role of absent) {
    const fallback = ROLE_FALLBACK[role];
    out[role] = fallback === undefined ? platformDefault.modes[mode][role] : out[fallback];
  }
  return out;
}

function categorical(value: unknown): CategoricalColor[] {
  if (!Array.isArray(value) || value.length !== 8) fail();
  return value.map((entry) => {
    const c = obj(entry);
    return {
      fill: colour(c.fill, false),
      ink: colour(c.ink, false),
      soft: colour(c.soft, false),
      line: colour(c.line, false),
    };
  });
}

/**
 * A value Emotion writes into CSS verbatim (a box-shadow, a font family / stack). Anything that
 * could close the declaration or rule, open markup, or pull a resource rejects the document.
 */
const CSS_BREAKOUT = /[;{}<>\\]|url\(/i;

function cssValue(value: unknown): string {
  const raw = str(value);
  if (CSS_BREAKOUT.test(raw)) fail();
  return raw;
}

function elevation(value: unknown): Elevation {
  const e = obj(value);
  return { card: cssValue(e.card), raised: cssValue(e.raised), dialog: cssValue(e.dialog) };
}

const GOOGLE_FONTS_ORIGIN = 'https://fonts.googleapis.com';

/** A stylesheet URL on Google Fonts (the only font host the fleet CSP admits), else `null`. */
function googleFontsUrl(value: unknown): string | null {
  const raw = url(value);
  if (raw === null) return null;
  try {
    return new URL(raw).origin === GOOGLE_FONTS_ORIGIN ? raw : null;
  } catch {
    return null;
  }
}

function font(value: unknown): BrandFont {
  const f = obj(value);
  const cssUrl = googleFontsUrl(f.cssUrl);
  // A google font without a usable stylesheet URL renders from its stack, like a system font.
  const source = f.source === 'google' && cssUrl !== null ? 'google' : 'system';
  return {
    family: cssValue(f.family),
    stack: cssValue(f.stack),
    source,
    cssUrl: source === 'google' ? cssUrl : null,
    files: Array.isArray(f.files) ? f.files.filter((x): x is string => typeof x === 'string') : [],
    weights: Array.isArray(f.weights)
      ? f.weights.filter((x): x is number => typeof x === 'number')
      : [],
  };
}

function assets(value: unknown): BrandAssets {
  const a = obj(value);
  const favicon = obj(a.favicon);
  const illustration = obj(a.onboardingIllustration);
  return {
    logo: url(a.logo),
    logoReversed: url(a.logoReversed),
    mark: url(a.mark),
    favicon: { svg: url(favicon.svg), png: url(favicon.png) },
    appleTouchIcon: url(a.appleTouchIcon),
    maskableIcon: url(a.maskableIcon),
    hero: url(a.hero),
    onboardingIllustration: { light: url(illustration.light), dark: url(illustration.dark) },
    emailHeader: url(a.emailHeader),
    socialImage: url(a.socialImage),
  };
}

function copy(value: unknown): BrandCopy {
  const c = obj(value);
  const locales: Record<string, BrandCopyStrings> = {};
  for (const [locale, entry] of Object.entries(obj(c.locales))) {
    const e = obj(entry);
    const strings = {} as BrandCopyStrings;
    for (const key of COPY_KEYS) strings[key] = nullableStr(e[key]);
    locales[locale] = strings;
  }
  const untranslated: Record<string, string[]> = {};
  if (c.untranslated && typeof c.untranslated === 'object') {
    for (const [locale, keys] of Object.entries(c.untranslated as Record<string, unknown>)) {
      if (Array.isArray(keys)) {
        untranslated[locale] = keys.filter((k): k is string => typeof k === 'string');
      }
    }
  }
  return { defaultLocale: str(c.defaultLocale), locales, untranslated };
}

function contrast(value: unknown): BrandTheme['contrast'] {
  const c = obj(value);
  if (typeof c.ok !== 'boolean') fail();
  const failures = Array.isArray(c.failures)
    ? c.failures.filter((f): f is ContrastFailure => !!f && typeof f === 'object')
    : [];
  return { ok: c.ok, failures };
}

export type BrandThemeValidation = { ok: true; theme: BrandTheme } | { ok: false; reason: string };

/**
 * Validate a v1 document into a `BrandTheme`, or the reason it is rejected WHOLE: a wrong
 * `schemaVersion`, a role missing or not a valid colour in either mode, a categorical set that is
 * not 8 per mode, a non-finite radius (`missing role 'accentSoft' in mode 'dark'`). Unknown keys
 * are ignored (new keys arrive without a bump); an absent optional role is filled in
 * (`OPTIONAL_ROLES`): `scrim` from the built-in default, `brandBoundary` from the document's `link`.
 */
export function validateBrandTheme(json: unknown): BrandThemeValidation {
  try {
    const doc = section('document', () => obj(json));
    if (doc.schemaVersion !== BRAND_THEME_SCHEMA_VERSION) {
      return { ok: false, reason: `unsupported schemaVersion ${String(doc.schemaVersion)}` };
    }
    const modes = section('modes', () => obj(doc.modes));
    const cat = section('categorical', () => obj(doc.categorical));
    const elev = section('elevation', () => obj(doc.elevation));
    const radius = section('radius', () => obj(doc.radius));
    const fonts = section('fonts', () => obj(doc.fonts));
    const theme: BrandTheme = {
      schemaVersion: 1,
      themeVersion: section('themeVersion', () => str(doc.themeVersion)),
      updatedAt: section('updatedAt', () => nullableStr(doc.updatedAt)),
      source: section('source', () => str(doc.source)),
      modes: { light: roles(modes.light, 'light'), dark: roles(modes.dark, 'dark') },
      categorical: {
        light: section(`categorical in mode 'light'`, () => categorical(cat.light)),
        dark: section(`categorical in mode 'dark'`, () => categorical(cat.dark)),
      },
      elevation: {
        light: section(`elevation in mode 'light'`, () => elevation(elev.light)),
        dark: section(`elevation in mode 'dark'`, () => elevation(elev.dark)),
      },
      radius: section('radius', () => ({
        style: typeof radius.style === 'string' ? radius.style : 'rounded',
        control: px(radius.control),
        card: px(radius.card),
        dialog: px(radius.dialog),
      })),
      fonts: {
        body: section('fonts.body', () => font(fonts.body)),
        heading: section('fonts.heading', () => font(fonts.heading)),
        mono: section('fonts.mono', () => font(fonts.mono)),
      },
      assets: section('assets', () => assets(doc.assets)),
      copy: section('copy', () => copy(doc.copy)),
      contrast: section('contrast', () => contrast(doc.contrast)),
    };
    return { ok: true, theme };
  } catch (error) {
    if (error instanceof Malformed) return { ok: false, reason: error.message };
    throw error;
  }
}

/** A valid v1 document as a `BrandTheme`, or `null` when `validateBrandTheme` rejects it. */
export function normalizeBrandTheme(json: unknown): BrandTheme | null {
  const result = validateBrandTheme(json);
  return result.ok ? result.theme : null;
}

/**
 * Whether a brand font's family is Inter (trimmed, case-insensitive) — TMM-T1 (TMMAPP-0084). Such
 * a face renders from the bundled `inter-ui` copy (`InterVariable`, `src/theme/fonts.ts`), which
 * carries `cv05` / `cv08`; the Google Fonts copy strips them.
 */
export const isInter = (family: string): boolean => family.trim().toLowerCase() === 'inter';

/**
 * Whether a brand font's family is JetBrains Mono (trimmed, case-insensitive) — TMM-T1
 * (TMMAPP-0085). Such a face renders from the bundled `@fontsource-variable/jetbrains-mono` copy
 * (`JetBrains Mono Variable`, `src/theme/fonts.ts`), so its Google stylesheet is not loaded.
 */
export const isJetBrainsMono = (family: string): boolean =>
  family.trim().toLowerCase() === 'jetbrains mono';

/**
 * Whether a brand font's family is Figtree (trimmed, case-insensitive) — the v2 body face
 * (TMMAPP-0150). Such a face renders from the bundled `@fontsource-variable/figtree` copy
 * (`Figtree Variable`, `src/theme/fonts.ts`).
 */
export const isFigtree = (family: string): boolean => family.trim().toLowerCase() === 'figtree';

/**
 * Whether a brand font's family is Newsreader (trimmed, case-insensitive) — the v2 heading face
 * (TMMAPP-0150). Such a face renders from the bundled `@fontsource-variable/newsreader` copy
 * (`Newsreader Variable`, with the optical-size axis, `src/theme/fonts.ts`).
 */
export const isNewsreader = (family: string): boolean =>
  family.trim().toLowerCase() === 'newsreader';

/** A face that renders from a bundled copy: its Google stylesheet is never requested. */
const isBundled = (family: string): boolean =>
  isInter(family) || isJetBrainsMono(family) || isFigtree(family) || isNewsreader(family);

/**
 * Distinct stylesheet URLs of the brand's `google` fonts, in body / heading / mono order. A face
 * whose family is Inter, JetBrains Mono, Figtree or Newsreader is left out (it renders from the
 * bundled copy, TMMAPP-0084 / 0085 / 0150) — unless its URL is shared with a face that is none of
 * them, which still needs it.
 */
export function brandFontUrls(theme: BrandTheme): string[] {
  const { body, heading, mono } = theme.fonts;
  const urls = [body, heading, mono]
    .filter((font) => font.source === 'google' && font.cssUrl !== null && !isBundled(font.family))
    .map((font) => font.cssUrl as string);
  return [...new Set(urls)];
}

/**
 * `failed` carries the diagnostic `reason` (TMMAPP-0037) — `rejected: …`, `fetch failed: <status>
 * <code>` or `revalidation failed (CORS?): <message>` — logged only outside production.
 */
export type BrandThemeFetchResult =
  | { status: 'ok'; theme: BrandTheme; etag: string }
  | { status: 'not-modified' }
  | { status: 'failed'; reason: string };

function currentHost(): string {
  return typeof location !== 'undefined' ? location.hostname : '';
}

/** The envelope's `error.code` of a failed response, if it carries one. */
async function errorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { error?: { code?: unknown } } | null;
    return typeof body?.error?.code === 'string' ? body.error.code : null;
  } catch {
    return null;
  }
}

/**
 * One read of the resolved theme. `If-None-Match` when an ETag is known (a `304` is
 * `not-modified`); a 404 `HOSTNAME_NOT_REGISTERED`, a 400, a transport failure, an unparseable or
 * invalid body are all `failed` — the caller falls back, never blanks.
 */
export async function fetchBrandTheme(
  authOrigin: string,
  { etag, host = currentHost() }: { etag?: string | null; host?: string } = {},
): Promise<BrandThemeFetchResult> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (etag) headers['If-None-Match'] = etag;
  let response: Response;
  try {
    response = await fetch(`${authOrigin}/api/branding/theme?host=${encodeURIComponent(host)}`, {
      credentials: 'omit',
      headers,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // `If-None-Match` needs a CORS preflight; a revalidation blocked by it surfaces as this.
    return {
      status: 'failed',
      reason: etag ? `revalidation failed (CORS?): ${message}` : `fetch failed: ${message}`,
    };
  }
  if (response.status === 304) return { status: 'not-modified' };
  if (!response.ok) {
    const code = await errorCode(response);
    return {
      status: 'failed',
      reason: `fetch failed: ${response.status}${code === null ? '' : ` ${code}`}`,
    };
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { status: 'failed', reason: 'rejected: the body is not JSON' };
  }
  const validation = validateBrandTheme(body);
  if (!validation.ok) return { status: 'failed', reason: `rejected: ${validation.reason}` };
  const { theme } = validation;
  // TMMAPP-0039: the ETag header is never read. The edge appends an encoding suffix
  // (`"<themeVersion>-gzip"`); only the body's version answers 304
  // (docs/handoffs/REPLY-from-saas-auth-brand-theme-followups-2026-09-26.md §1).
  return { status: 'ok', theme, etag: brandThemeEtag(theme) };
}

/** The validator a theme is revalidated with: its body `themeVersion`, quoted. */
export function brandThemeEtag(theme: BrandTheme): string {
  return `"${theme.themeVersion}"`;
}

export const BRAND_THEME_STORAGE_KEY = 'tmm_brand_theme';

export interface CachedBrandTheme {
  host: string;
  etag: string;
  theme: BrandTheme;
}

/**
 * The cached theme for `host`, re-validated; another host's cache or a bad entry is ignored. An
 * entry whose `etag` is not exactly the body-derived `"<themeVersion>"` (a header value such as
 * `"v1-gzip"`, written by a build that read the ETag header) is healed in place — rewritten, never
 * discarded — so the next revalidation can answer 304 (TMMAPP-0039).
 */
export function readCachedBrandTheme(host = currentHost()): CachedBrandTheme | null {
  try {
    const raw = localStorage.getItem(BRAND_THEME_STORAGE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { host?: unknown; etag?: unknown; doc?: unknown };
    if (entry.host !== host) return null;
    const theme = normalizeBrandTheme(entry.doc);
    if (!theme) return null;
    const etag = brandThemeEtag(theme);
    if (entry.etag !== etag) writeCachedBrandTheme(host, etag, theme);
    return { host, etag, theme };
  } catch {
    return null;
  }
}

export function writeCachedBrandTheme(host: string, etag: string, theme: BrandTheme): void {
  try {
    localStorage.setItem(BRAND_THEME_STORAGE_KEY, JSON.stringify({ host, etag, doc: theme }));
  } catch {
    /* storage unavailable — the theme still applies for this visit */
  }
}

/** How long a first visit (no cache) waits for the theme before painting the built-in default. */
export const FIRST_VISIT_WAIT_MS = 1500;

const LOG_PREFIX = '[brand-theme]';

/**
 * Whether brand-theme diagnostics log (TMMAPP-0037). Fails CLOSED: on in development, otherwise
 * only when neither the build flag nor the bootstrap's deploy env says production (the
 * bootstrap value trimmed and case-folded — a server string is never trusted verbatim).
 */
export function brandDiagnosticsEnabled({
  dev,
  buildEnv,
  bootstrapEnv,
}: {
  dev: boolean;
  buildEnv: string;
  bootstrapEnv: string | null;
}): boolean {
  if (dev) return true;
  const isProduction = (value: string | null) => {
    const v = value?.trim().toLowerCase();
    return v === 'production' || v === 'prod';
  };
  return !isProduction(buildEnv) && !isProduction(bootstrapEnv);
}

let warnedVersion: string | null = null;
/** Failure kinds (`rejected`, `fetch failed`, `revalidation failed (CORS?)`) already logged. */
const warnedFailures = new Set<string>();

/** Test-only: forget which diagnostics this "page load" has already logged. */
export function __resetBrandThemeDiagnosticsForTest(): void {
  warnedVersion = null;
  warnedFailures.clear();
}

/** Diagnostics only: name the tenant's failing pairs once per version; nothing is user-facing. */
function warnContrastOnce(theme: BrandTheme, diagnostics: boolean): void {
  if (!diagnostics || theme.contrast.ok || warnedVersion === theme.themeVersion) return;
  warnedVersion = theme.themeVersion;
  console.warn(`${LOG_PREFIX} contrast failures (admin-entered values):`, theme.contrast.failures);
}

/** Diagnostics only: why the theme was not applied — once per page load per failure kind. */
function warnFailureOnce(reason: string, diagnostics: boolean): void {
  const kind = reason.split(':')[0] ?? reason;
  if (!diagnostics || warnedFailures.has(kind)) return;
  warnedFailures.add(kind);
  console.warn(`${LOG_PREFIX} ${reason}`);
}

/**
 * Bring the brand theme up to date for this host. With a cached copy, resolves at once (the app
 * is already painting from the cache) and revalidates in the background with `If-None-Match` —
 * a new version is applied in place, a `304` changes nothing. Without one, waits at most
 * `waitMs` for the fetch; a later success is still applied when it lands.
 *
 * `diagnostics` (TMMAPP-0037: every non-production deploy, and development) logs why a document
 * was rejected or the fetch failed, and the tenant's contrast failures, with `console.warn`.
 * Production passes `false` and stays silent.
 */
export async function syncBrandTheme(
  authOrigin: string,
  apply: (theme: BrandTheme) => void,
  {
    host = currentHost(),
    waitMs = FIRST_VISIT_WAIT_MS,
    diagnostics = import.meta.env.DEV,
  }: { host?: string; waitMs?: number; diagnostics?: boolean } = {},
): Promise<void> {
  const cached = readCachedBrandTheme(host);
  if (cached) warnContrastOnce(cached.theme, diagnostics);
  const request = fetchBrandTheme(authOrigin, { etag: cached?.etag ?? null, host }).then(
    (result) => {
      if (result.status === 'failed') warnFailureOnce(result.reason, diagnostics);
      if (result.status !== 'ok') return;
      writeCachedBrandTheme(host, result.etag, result.theme);
      // The version already painted from the cache: refresh the cache, never re-apply (no
      // re-render, no font-link churn).
      if (cached?.theme.themeVersion === result.theme.themeVersion) return;
      warnContrastOnce(result.theme, diagnostics);
      apply(result.theme);
    },
    () => {
      /* the theme never fails the app — the current (cached or built-in) theme stays */
    },
  );
  if (cached) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    request,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, waitMs);
    }),
  ]);
  clearTimeout(timer);
}
